import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Message, ProvidentFund } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { EmployeeService } from 'src/app/shared/services/employee.service';

@Component({
  selector: 'app-provident-funds-crud',
  templateUrl: './provident-funds-crud.component.html',
  styleUrls: ['./provident-funds-crud.component.scss']
})
export class ProvidentFundsCrudComponent implements OnInit {
  @Input() providentFund: ProvidentFund;
  @Input() context: Context;
  @Input() providentFunds: ProvidentFund[] = [];
  public types: string[] = ['FIXED', 'PERCENTAGE'];
  public providentFundForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private employeeService: EmployeeService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setProvidentFundForm(this.providentFund);
    this.employeeService.getTypes().subscribe(types => {
      this.types = types.length ? types : this.types;
      this.cdr.detectChanges();
    });
    this.providentFundForm.get('type')?.valueChanges.subscribe((type) => {
      console.log('Type changed to:', type); // Debug log
      this.providentFundForm.get('employeeRatio')?.setValue(null, { emitEvent: false });
      this.updateValidators();
      this.calculateEmployerRatio();
      this.cdr.detectChanges();
    });
    this.providentFundForm.get('value')?.valueChanges.subscribe(() => {
      this.updateValidators(); // Update employeeRatio max validator
      this.calculateEmployerRatio();
    });
    this.providentFundForm.get('employeeRatio')?.valueChanges.subscribe(() => this.calculateEmployerRatio());
  }

  public setProvidentFundForm(providentFund?: ProvidentFund) {
    if (!providentFund) {
      providentFund = {
        id: null,
        name: null,
        label: null,
        type: 'FIXED',
        value: null,
        employeeRatio: null,
        employeerRatio: null,
        disabled: false,
        companyId: this.context?.companyId,
        companyName: null
      } as ProvidentFund;
    }
    this.providentFundForm = this.fb.group({
      id: this.fb.control(providentFund.id),
      name: this.fb.control(providentFund.name, [Validators.required, Validators.pattern(RegExp.name)]),
      label: this.fb.control(providentFund.label, [Validators.pattern(RegExp.label)]),
      value: this.fb.control(providentFund.value, [Validators.required, Validators.min(0.01)]),
      type: this.fb.control(providentFund.type, [Validators.required]),
      disabled: this.fb.control(providentFund.disabled),
      employeeRatio: this.fb.control(providentFund.employeeRatio, [Validators.required, Validators.min(0)]),
      employeerRatio: this.fb.control(providentFund.employeerRatio, [Validators.required, Validators.min(0)]),
      companyId: this.fb.control(providentFund.companyId, [Validators.required]),
      companyName: this.fb.control(providentFund.companyName)
    });
    this.updateValidators();
    this.providentFundForm.valueChanges.subscribe(() => this.modified = true);
  }

  get f() {
    return this.providentFundForm.controls;
  }

  public getIconClass(field: string): string {
    const type = this.f.type.value;
    if (field === 'ratio') {
      return type === 'FIXED' ? 'bx bx-rupee' : 'percent-icon';
    }
    return 'bx bx-rupee'; // Fallback
  }

  public onTypeChange(event: any) {
    console.log('onTypeChange triggered:', event); // Debug log
    this.providentFundForm.get('employeeRatio')?.setValue(null, { emitEvent: false });
    this.updateValidators();
    this.calculateEmployerRatio();
    this.cdr.detectChanges();
  }

  public updateValidators() {
    const type = this.providentFundForm.get('type')?.value;
    const employeeRatioControl = this.providentFundForm.get('employeeRatio');
    const valueControl = this.providentFundForm.get('value');
    const value = this.providentFundForm.get('value')?.value || 1000000;

    if (type === 'FIXED') {
      employeeRatioControl?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(value)
      ]);
      valueControl?.setValidators([Validators.required, Validators.min(0.01)]);
    } else if (type === 'PERCENTAGE') {
      employeeRatioControl?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(99)
      ]);
      valueControl?.setValidators([Validators.required, Validators.min(0.01)]);
    }
    employeeRatioControl?.updateValueAndValidity();
    valueControl?.updateValueAndValidity();
  }

  public calculateEmployerRatio() {
    const type = this.providentFundForm.get('type')?.value;
    const value = this.providentFundForm.get('value')?.value;
    const employeeRatio = this.providentFundForm.get('employeeRatio')?.value;
    let employerRatio = 0;

    if (type === 'FIXED' && value && employeeRatio !== null) {
      employerRatio = value - employeeRatio;
    } else if (type === 'PERCENTAGE' && employeeRatio !== null) {
      employerRatio = 100 - employeeRatio;
    }

    this.providentFundForm.get('employeerRatio')?.setValue(employerRatio >= 0 ? employerRatio : 0, { emitEvent: false });
  }

  public restrictInput(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const type = this.providentFundForm.get('type')?.value;
    const value = this.providentFundForm.get('value')?.value;

    if (type === 'FIXED' && value) {
      const currentValue = input.value;
      const newValue = currentValue + event.key;
      if (parseFloat(newValue) > value) {
        event.preventDefault();
      }
    } else if (type === 'PERCENTAGE') {
      const currentValue = input.value;
      const newValue = currentValue + event.key;
      if (parseFloat(newValue) > 99 || newValue.length > 2) {
        event.preventDefault();
      }
    }
  }

  public saveProvidentFund(inProgress?: any) {
    this.submitted = true;
    if (this.providentFundForm.valid) {
      const providentFund = this.providentFundForm.value as ProvidentFund;
      if (this.providentFunds.some(t => t.name.toLowerCase() === providentFund.name.toLowerCase() && t.id !== providentFund.id)) {
        this.commonService.setToastr(400, `Cannot ${providentFund?.id ? 'update' : 'add'} the record. ${providentFund.name} already exists.`);
        return;
      }
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Provident Fund ${providentFund.name} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (providentFund.id) {
        this.employeeService.updateProvidentFund(providentFund.id, providentFund).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.activeModal.close(response);
              this.commonService.setToastr(200, `Provident Fund ${providentFund.name} has been updated successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          }
        });
      } else {
        this.employeeService.createProvidentFund(providentFund).subscribe({
          next: (response: ProvidentFund) => {
            if (response) {
              this.activeModal.close(response);
              this.commonService.setToastr(200, `Provident Fund ${providentFund.name} has been created successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          }
        });
      }
    }
  }
}